import { useId, useMemo, useState } from 'react'
import { Button, Dialog } from '@bloo-team/ui'
import { makeTermKey } from '@bloo-team/api-client'
import {
  BENIGN_TYPES,
  DISPOSITIONS,
  DISPOSITION_HINT,
  DISPOSITION_LABEL,
  TP_TYPES,
  type Disposition,
  type Signal,
} from '../../lib/signal-model'
import type { SuppressionTerms } from '../../lib/suppression'
import type { CloseDialogProps } from './types'

/**
 * Close with disposition (decisions S3, S4, S7). Six-way disposition;
 * FP-logic offers the legacy FP flag (default on) and a suppression rule
 * prefilled from the first signal's entities; FP-data flags the source
 * streams in the note. Closing always acts on the signals passed in
 * (decision S38: close-all-members not carried over).
 *
 * State lives in an inner form so it resets on every open.
 */

interface TermDraft {
  key: string
  include: boolean
  /** Comma-separated, editable. */
  values: string
}

function initialTerms(signal: Signal | undefined): TermDraft[] {
  if (!signal) return []
  const byKey = new Map<string, string[]>()
  for (const e of signal.entities) {
    const key = makeTermKey(e.role, e.field)
    byKey.set(key, [...(byKey.get(key) ?? []), e.value])
  }
  return [...byKey.entries()].map(([key, values]) => ({ key, include: true, values: values.join(', ') }))
}

function draftToTerms(drafts: TermDraft[]): SuppressionTerms {
  const out: SuppressionTerms = {}
  for (const d of drafts) {
    if (!d.include) continue
    const values = d.values.split(',').map((v) => v.trim()).filter(Boolean)
    if (values.length) out[d.key] = values
  }
  return out
}

export function CloseDialog({ open, signals, preset, onCancel, onConfirm }: CloseDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} title={signals.length === 1 ? 'Close signal' : `Close ${signals.length} signals`} className="sp-close-dialog">
      {open ? <CloseForm signals={signals} preset={preset} onCancel={onCancel} onConfirm={onConfirm} /> : null}
    </Dialog>
  )
}

function CloseForm({ signals, preset, onCancel, onConfirm }: Omit<CloseDialogProps, 'open'>) {
  const uid = useId()
  const first = signals[0]
  const [disposition, setDisposition] = useState<Disposition | null>(preset ?? null)
  const [dispositionType, setDispositionType] = useState<string>('')
  const [note, setNote] = useState('')
  const [flagFp, setFlagFp] = useState(true)
  // The suppress shortcut's whole point is the rule, so it opens expanded.
  const [suppress, setSuppress] = useState(preset === 'false_positive_logic')
  const [terms, setTerms] = useState<TermDraft[]>(() => initialTerms(first))

  const uuids = useMemo(() => signals.map((s) => s.uuid), [signals])

  const detectionId = first?.detectionId ?? null
  const detectionName = first?.detectionName ?? ''
  const streams = useMemo(() => [...new Set(signals.flatMap((s) => s.streams))], [signals])
  const isFpLogic = disposition === 'false_positive_logic'
  const isFpData = disposition === 'false_positive_data'
  const typeOptions =
    disposition === 'true_positive' ? TP_TYPES : disposition === 'benign_true_positive' ? BENIGN_TYPES : null

  const suppressionTerms = useMemo(() => draftToTerms(terms), [terms])
  const suppressionValid = !suppress || (detectionId !== null && Object.keys(suppressionTerms).length > 0)
  const noteValid = disposition === 'duplicate' || note.trim().length > 0
  const valid = disposition !== null && noteValid && suppressionValid && uuids.length > 0

  const names = useMemo(() => [...new Set(signals.map((s) => s.detectionName))], [signals])

  const confirm = () => {
    if (!valid || !disposition) return
    onConfirm({
      uuids,
      disposition,
      dispositionType: typeOptions ? dispositionType || null : null,
      note: note.trim(),
      flagFp: isFpLogic ? flagFp : isFpData ? flagFp : false,
      suppression:
        isFpLogic && suppress && detectionId !== null
          ? { wbId: detectionId, wbName: detectionName, terms: suppressionTerms }
          : null,
    })
  }

  return (
    <div className="hud-stack sp-form">
      <p className="sp-form-what">
        Closing <b>{uuids.length}</b> signal{uuids.length === 1 ? '' : 's'}
        {names.length ? ` — ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${names.length - 3} more` : ''}` : ''}
      </p>

      <fieldset className="sp-fieldset">
        <legend className="hud-eyebrow">disposition</legend>
        {DISPOSITIONS.map((d) => (
          <label key={d} className="bui-check sp-disp" htmlFor={`${uid}-d-${d}`}>
            <input
              id={`${uid}-d-${d}`}
              aria-label={DISPOSITION_LABEL[d]}
              type="radio"
              name={`${uid}-disposition`}
              value={d}
              checked={disposition === d}
              onChange={() => {
                setDisposition(d)
                setDispositionType('')
              }}
            />
            <span>
              {DISPOSITION_LABEL[d]}
              <small className="sp-disp-hint">{DISPOSITION_HINT[d]}</small>
            </span>
          </label>
        ))}
      </fieldset>

      {typeOptions ? (
        <label className="hud-stack sp-field" htmlFor={`${uid}-type`}>
          <span className="hud-eyebrow">type</span>
          <select id={`${uid}-type`} className="bui-input" value={dispositionType} onChange={(e) => setDispositionType(e.target.value)}>
            <option value="">— pick a type —</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
      ) : null}

      {isFpData ? (
        <p className="q-facet-note" role="status">
          The source stream{streams.length === 1 ? '' : 's'} {streams.length ? streams.join(', ') : '(unknown)'} will be flagged in the note.
        </p>
      ) : null}

      {isFpLogic || isFpData ? (
        <label className="bui-check" htmlFor={`${uid}-flag`}>
          <input id={`${uid}-flag`} aria-label="Flag as false positive on the backend" type="checkbox" checked={flagFp} onChange={(e) => setFlagFp(e.target.checked)} />
          <span>Flag as false positive on the backend</span>
        </label>
      ) : null}

      {isFpLogic ? (
        <div className="hud-stack sp-suppress">
          <label className="bui-check" htmlFor={`${uid}-sup`}>
            <input
              id={`${uid}-sup`}
              aria-label="Create suppression rule"
              type="checkbox"
              checked={suppress}
              disabled={detectionId === null}
              onChange={(e) => setSuppress(e.target.checked)}
            />
            <span>Create suppression rule</span>
          </label>
          {detectionId === null ? (
            <p className="q-facet-note">This signal carries no detection id — a rule cannot be attached to it.</p>
          ) : null}
          {suppress && detectionId !== null ? (
            <div className="hud-stack sp-terms">
              <span className="q-facet-note">
                Rule on <b>{detectionName}</b> — keys are AND'd, values within a key are OR'd.
              </span>
              {terms.length === 0 ? <p className="q-facet-note">No entities to build terms from.</p> : null}
              {terms.map((t, i) => (
                <div key={t.key} className="hud-row sp-term">
                  <label className="bui-check" htmlFor={`${uid}-t-${i}`}>
                    <input
                      id={`${uid}-t-${i}`}
                      type="checkbox"
                      checked={t.include}
                      aria-label={`Include ${t.key}`}
                      onChange={(e) => setTerms((ts) => ts.map((x, j) => (j === i ? { ...x, include: e.target.checked } : x)))}
                    />
                    <span className="mono">{t.key}</span>
                  </label>
                  <input
                    className="bui-input"
                    aria-label={`Values for ${t.key}`}
                    value={t.values}
                    disabled={!t.include}
                    onChange={(e) => setTerms((ts) => ts.map((x, j) => (j === i ? { ...x, values: e.target.value } : x)))}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <label className="hud-stack sp-field" htmlFor={`${uid}-note`}>
        <span className="hud-eyebrow">note{disposition === 'duplicate' ? '' : ' · required'}</span>
        <textarea
          id={`${uid}-note`}
          aria-label="Note"
          className="bui-input"
          rows={3}
          placeholder="why this disposition — the next analyst reads this"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className="hud-row sp-form-actions">
        <Button variant="ghost" size="sm" tone="neutral" onPress={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" isDisabled={!valid} onPress={confirm}>
          Close {uuids.length === 1 ? 'signal' : `${uuids.length} signals`}
        </Button>
      </div>
    </div>
  )
}
